import useElementConfig, {
  UseElementConfigParams,
  UseElementConfigReturn,
  ConfigSchema,
  ConfigValidator,
  ConfigTransformer,
} from "./useElementConfig"
import { getElementConfigSetup } from "utils/elementConfigSchemas"
import {
  DataElementConfig,
  SlideElement,
  ELEMENT_TYPE,
} from "types/ReportingDashboard/elements.types"

/**
 * Element config setup from schema utility
 */
interface ElementConfigSetup {
  defaultConfig: DataElementConfig
  configSchema: ConfigSchema
  configValidator?: ConfigValidator
  configTransformer?: ConfigTransformer
}

/**
 * Hook options that extend the base useElementConfig params
 */
interface ElementConfigHookOptions
  extends Omit<
    UseElementConfigParams,
    "defaultConfig" | "configSchema" | "configValidator" | "configTransformer"
  > {
  autoSave?: boolean
  saveDelay?: number
}

/**
 * Dashboard-specific config properties
 */
interface DashboardConfig {
  isDashboard: boolean
  dashboardFilters: string[]
  dashboardSettings: Record<string, unknown>
}

/**
 * Data element config properties
 */
interface DataElementConfigExtensions {
  lastDataFetch: number | null
  dataSourceId: string | null
  autoRefresh: boolean
  refreshInterval: number
}

/**
 * Type for element config hook function
 */
type ElementConfigHook = (
  hookOptions: ElementConfigHookOptions
) => UseElementConfigReturn

/**
 * Factory function that creates element-type-specific config hooks
 * This simplifies usage by pre-configuring the hook for specific element types
 *
 * @param elementType - The type of element to create a config hook for
 * @returns A configured hook function for the specified element type
 */
export const createElementConfigHook = (
  elementType: string
): ElementConfigHook => {
  const configSetup: ElementConfigSetup = getElementConfigSetup(elementType)

  return (
    hookOptions: ElementConfigHookOptions = {} as ElementConfigHookOptions
  ): UseElementConfigReturn => {
    const {
      elementId,
      config,
      onConfigUpdate,
      autoSave = true,
      saveDelay = 700,
      ...otherOptions
    } = hookOptions

    return useElementConfig({
      elementId,
      config,
      onConfigUpdate,
      autoSave,
      saveDelay,
      ...configSetup,
      ...otherOptions,
    })
  }
}

/**
 * Pre-created hooks for each element type
 * These can be imported directly for convenience
 */
export const useChartConfig: ElementConfigHook = createElementConfigHook(
  ELEMENT_TYPE.CHART
)
export const usePieChartConfig: ElementConfigHook = createElementConfigHook(
  ELEMENT_TYPE.PIE_CHART
)
export const useTimeSeriesConfig: ElementConfigHook = createElementConfigHook(
  ELEMENT_TYPE.TIME_SERIES_CHART
)
export const useTableConfig: ElementConfigHook = createElementConfigHook(
  ELEMENT_TYPE.TABLE
)
export const useTextConfig: ElementConfigHook = createElementConfigHook(
  ELEMENT_TYPE.TEXT
)
export const useImageConfig: ElementConfigHook = createElementConfigHook(
  ELEMENT_TYPE.IMAGE
)

/**
 * Options for auto element config hook
 */
type AutoElementConfigOptions = Partial<
  Omit<ElementConfigHookOptions, "elementId">
>

/**
 * Generic hook that automatically detects element type
 * Falls back to base configuration if type is not recognized
 *
 * @param element - The slide element to configure
 * @param config - Current configuration
 * @param onConfigUpdate - Config update callback
 * @param options - Additional options
 * @returns Element configuration hook result
 */
export const useAutoElementConfig = (
  element: SlideElement | null | undefined,
  config: DataElementConfig | null | undefined,
  onConfigUpdate: (config: DataElementConfig | null) => void,
  options: AutoElementConfigOptions = {}
): UseElementConfigReturn => {
  const elementType = element?.type || ELEMENT_TYPE.DEFAULT
  const configHook = createElementConfigHook(elementType)

  return configHook({
    elementId: element?.id || "unknown",
    config,
    onConfigUpdate,
    ...options,
  })
}

/**
 * Options for dashboard element config hook
 */
type DashboardElementConfigOptions = Partial<ElementConfigHookOptions> & {
  elementId: string
  config: DataElementConfig | null | undefined
  onConfigUpdate: (config: DataElementConfig | null) => void
  // Additional dashboard-specific options can be added here
  dashboardMode?: boolean
}

/**
 * Hook specifically for elements that need dashboard-specific config
 * Adds dashboard-related config properties and validation
 *
 * @param elementType - Type of element
 * @param hookOptions - Hook configuration options
 * @returns Element configuration hook result with dashboard extensions
 */
export const useDashboardElementConfig = (
  elementType: string,
  hookOptions: DashboardElementConfigOptions
): UseElementConfigReturn => {
  const baseConfigSetup = getElementConfigSetup(elementType)

  // Extend config with dashboard-specific properties
  const dashboardConfigSetup: ElementConfigSetup = {
    ...baseConfigSetup,
    defaultConfig: {
      ...baseConfigSetup.defaultConfig,
      isDashboard: true,
      dashboardFilters: [],
      dashboardSettings: {},
    } as DataElementConfig & DashboardConfig,
    configSchema: {
      ...baseConfigSetup.configSchema,
      isDashboard: { required: false, type: "boolean", default: true },
      dashboardFilters: { required: false, type: "array", default: [] },
      dashboardSettings: { required: false, type: "object", default: {} },
    },
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { dashboardMode, ...restOptions } = hookOptions

  return useElementConfig({
    ...dashboardConfigSetup,
    ...restOptions,
  })
}

/**
 * Options for data element config hook
 */
type DataElementConfigOptions = Partial<ElementConfigHookOptions> & {
  elementId: string
  config: DataElementConfig | null | undefined
  onConfigUpdate: (config: DataElementConfig | null) => void
  // Additional data-specific options can be added here
  enableAutoRefresh?: boolean
}

/**
 * Utility hook for elements that need to sync with external data sources
 * Adds data-loading specific configuration and state management
 *
 * @param elementType - Type of element
 * @param hookOptions - Hook configuration options
 * @returns Element configuration hook result with data extensions
 */
export const useDataElementConfig = (
  elementType: string,
  hookOptions: DataElementConfigOptions
): UseElementConfigReturn => {
  const baseConfigSetup = getElementConfigSetup(elementType)

  const dataConfigSetup: ElementConfigSetup = {
    ...baseConfigSetup,
    defaultConfig: {
      ...baseConfigSetup.defaultConfig,
      lastDataFetch: null,
      dataSourceId: null,
      autoRefresh: false,
      refreshInterval: 300000, // 5 minutes
    } as DataElementConfig & DataElementConfigExtensions,
    configSchema: {
      ...baseConfigSetup.configSchema,
      lastDataFetch: { required: false, type: "number" },
      dataSourceId: { required: false, type: "string" },
      autoRefresh: { required: false, type: "boolean", default: false },
      refreshInterval: { required: false, type: "number", default: 300000 },
    },
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { enableAutoRefresh, ...restOptions } = hookOptions

  return useElementConfig({
    ...dataConfigSetup,
    ...restOptions,
  })
}

/**
 * Example usage in components:
 *
 * // Simple usage - automatically configured for chart elements
 * const chartConfig = useChartConfig({
 *   elementId: element.id,
 *   config: config,
 *   onConfigUpdate: onConfigUpdate,
 * })
 *
 * // More complex usage with custom options
 * const pieConfig = usePieChartConfig({
 *   elementId: element.id,
 *   config: config,
 *   onConfigUpdate: onConfigUpdate,
 *   autoSave: false, // Disable auto-save
 *   saveDelay: 1000, // Custom save delay
 * })
 *
 * // Auto-detection of element type
 * const autoConfig = useAutoElementConfig(element, config, onConfigUpdate)
 *
 * // Dashboard-specific config
 * const dashboardConfig = useDashboardElementConfig(ELEMENT_TYPE.CHART, {
 *   elementId: element.id,
 *   config: config,
 *   onConfigUpdate: onConfigUpdate,
 *   dashboardMode: true,
 * })
 */
